import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { buildCsv, downloadCsv } from "@/lib/csv";
import { savePdfA4Portrait_Scoresheet } from "@/lib/pdf";
import { formatDMY } from "@/lib/date";
import { usePrintStylesheet } from "@/pages/printStyles";

export default function PrintScoresheet() {
  const { competitionId } = useParams();
  const [sp] = useSearchParams();
  // Default to "entries" so the sheet reflects the actual competition roster
  // (every saved entry, including photographers who entered multiple times).
  // Pass ?mode=blank to fall back to "one row per active photographer".
  const mode = sp.get("mode") || "entries";
  const [data, setData] = useState(null);

  usePrintStylesheet();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/print/scoresheet/${competitionId}`, { params: { mode } });
      setData(data);
    })();
  }, [competitionId, mode]);

  // Ensure the browser's print header / tab title carries the competition
  // name — otherwise operating-system chrome shows the raw URL instead.
  useEffect(() => {
    if (!data?.competition?.competition_name) return;
    const prev = document.title;
    document.title = `${data.competition.competition_name} — Score Sheet`;
    return () => { document.title = prev; };
  }, [data]);

  if (!data) return <div style={{ padding: 40, color: "#555", background: "#fff" }}>Loading sheet…</div>;
  const { competition, season, rows, club_name: clubName } = data;

  const exportCsv = () => {
    const headers = ["#", "I.D.", "Photographer", "Image Title", "Score", "Place"];
    const csvRows = rows.map((r, i) => ({
      "#": i + 1,
      "I.D.": r.photographer_code,
      Photographer: r.photographer_name,
      "Image Title": r.image_title || "",
      Score: "",
      Place: "",
    }));
    const safe = (competition.competition_name || "scoresheet").replace(/[^a-z0-9-_]+/gi, "_");
    downloadCsv(`${safe}_scoresheet.csv`, buildCsv(headers, csvRows));
  };

  return (
    <div className="cc-print">
      <div className="cc-print-toolbar">
        <button
          onClick={() => savePdfA4Portrait_Scoresheet({ competition, season, rows, clubName })}
          className="cc-print-btn primary"
          data-testid="scoresheet-print-button"
        >
          Print (PDF)
        </button>
        <button onClick={exportCsv} className="cc-print-btn" data-testid="scoresheet-export-csv-button">Export CSV</button>
        <button onClick={() => window.close()} className="cc-print-btn">Close</button>
      </div>

      <header className="cc-print-header">
        {clubName ? <div className="club-name">{clubName}</div> : null}
        <div className="comp-title">{competition.competition_name}</div>
        <div className="meta-row">
          <span><strong>Date:</strong> {formatDMY(competition.competition_date)}</span>
          <span><strong>Type:</strong> {competition.competition_type}</span>
          <span><strong>Category:</strong> {competition.category}</span>
          {competition.round_number ? <span><strong>Round:</strong> {competition.round_number}</span> : null}
          {competition.judge_name ? <span><strong>Judge:</strong> {competition.judge_name}</span> : null}
          {season ? <span><strong>Season:</strong> {season.season_name}</span> : null}
        </div>
        <div className="page-sub">Judging Score Sheet</div>
      </header>

      <table className="cc-print-table">
        <colgroup>
          <col style={{ width: "6%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "23%" }} />
          <col style={{ width: "40%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>#</th><th>I.D.</th><th>Photographer</th><th>Image Title</th><th>Score</th><th>Place</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.photographer_code}-${i}`}>
              <td className="num">{i + 1}</td>
              <td className="mono">{r.photographer_code}</td>
              <td>{r.photographer_name}</td>
              <td>{r.image_title || ""}</td>
              <td></td>
              <td></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="cc-print-legend">
        <div><strong>Place codes:</strong> 1 = 1st &nbsp; 2 = 2nd &nbsp; 3 = 3rd &nbsp; HC = Highly Commended &nbsp; C = Commended</div>
      </div>

      <footer className="cc-print-footer">
        Camera Club Score · printed {new Date().toLocaleString()}
      </footer>
    </div>
  );
}
